/**
 * Shared Tailwind class bundles for data tables (tenant admin).
 * Keep in sync with web/lib/dashboardUiClasses.ts where applicable.
 */

import { bgLayer2, cardLayer1 } from './layerClasses';

export const tableCardClass = `${cardLayer1} overflow-hidden`;

/**
 * Shared top strip (background + bottom border). Single source for toolbars,
 * design filters, Financial "Detalles" title row, etc.
 *
 * Uses `bgLayer2` so toolbar strips and layer-2 nested cards share the same
 * translucent surface (the "lighter inside layer-1" tone).
 */
export const tableToolbarStripClass =
  `border-b border-border-subtle dark:border-border-subtle-dark ${bgLayer2}`;

/**
 * Standard width wrapper for the search `<Input>` inside `tableToolbarClass`.
 * Caps at 19.6rem (~314px) on desktop so right-side toolbar controls (filters,
 * page-size, view switch, primary CTA button) always have room to fit.
 *
 * Apply to the wrapping `<div>` around the search Input, not to the Input itself.
 */
export const tableToolbarSearchClass =
  'flex-1 md:max-w-[19.6rem] min-w-0 w-full md:w-auto';

/**
 * Search / filter row inside `tableCardClass`. Composes `tableToolbarStripClass`.
 * Two-tone with `theadClass` (solid slate band below).
 */
export const tableToolbarClass = `${tableToolbarStripClass} p-4 flex flex-col md:flex-row gap-4 items-center justify-between`;

/**
 * Title row above a data table (e.g. Financial detail). Same strip as `tableToolbarClass`, wider padding.
 */
export const tableDetailTitleRowClass = `${tableToolbarStripClass} px-6 py-4 flex items-center`;

/**
 * Shared surface for ALL interactive controls inside `tableToolbarClass`.
 * Contrasts against the semi-transparent toolbar strip.
 */
const toolbarControlSurface =
  'bg-surface-light dark:bg-surface-dark/60 border-border-strong dark:border-border-strong-dark';

/**
 * Size / weight / leading — matches the date picker label row (no color).
 */
export const tableToolbarTypographyClass = 'text-sm font-medium leading-tight';

/**
 * Single warm brown/grey for ALL copy inside toolbar fields (dropdown label, select,
 * date value or placeholder, search text). Matches date picker “Seleccionar fecha” — never white in dark mode.
 */
export const tableToolbarFieldTextClass =
  `${tableToolbarTypographyClass} text-slate-500 dark:text-slate-400`;

/** @alias — same as field text (no separate “bright” value colour). */
export const tableToolbarValueTextClass = tableToolbarFieldTextClass;

/** @alias — identical to value so empty/filled look the same hue as the date control. */
export const tableToolbarPlaceholderTextClass = tableToolbarFieldTextClass;

/**
 * Material Symbol in toolbar controls — idle: slate-400 (same as date picker calendar).
 * When open/focused: add `tableToolbarIconOpenClass`.
 * On solid primary/amber triggers: add `!text-white`.
 */
export const tableToolbarIconClass =
  '!text-[18px] shrink-0 font-normal text-slate-400 dark:text-slate-400 transition-colors';

export const tableToolbarIconOpenClass = '!text-primary dark:!text-primary';

/**
 * Shared hover / focus / press feedback for toolbar controls.
 * Open popovers add `tableToolbarControlOpenClass` on top.
 */
export const tableToolbarControlInteractiveClass =
  'transition-[border-color,box-shadow,opacity,filter] duration-150 hover:border-border-strong dark:hover:border-border-strong-dark focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary active:opacity-90';

/** While dropdown/calendar is open (matches focus ring so behaviour feels the same). */
export const tableToolbarControlOpenClass = 'ring-1 ring-primary border-primary z-[1]';

/** Native `<select>` inside `tableToolbarClass`. */
export const tableToolbarSelectClass =
  `h-10 w-full rounded-md border ${toolbarControlSurface} ${tableToolbarValueTextClass} px-3 py-0 shadow-sm ${tableToolbarControlInteractiveClass} min-w-0 cursor-pointer`;

/**
 * Custom dropdown trigger (estado, etiquetas, pendientes…).
 * Same height and typography as `tableToolbarSelectClass`.
 */
export const tableToolbarDropdownBtnClass =
  `h-10 w-full flex items-center justify-between gap-2 rounded-md border ${toolbarControlSurface} ${tableToolbarValueTextClass} px-3 py-0 shadow-sm ${tableToolbarControlInteractiveClass} min-w-0 cursor-pointer`;

/**
 * Filter applied (tags): solid primary; still gets open ring when menu is open.
 */
export const tableToolbarDropdownBtnActiveClass =
  '!text-white !bg-primary dark:!bg-primary !border-primary shadow-sm hover:brightness-110';

/**
 * Date field trigger in toolbar — same box as select/dropdown.
 */
export const tableToolbarDatePickerTriggerClass =
  `relative w-full h-10 flex items-center justify-between cursor-pointer rounded-md border ${toolbarControlSurface} px-3 py-0 shadow-sm ${tableToolbarControlInteractiveClass} min-w-0 text-left`;

/**
 * Toggle group (grid/list): same outer height as other toolbar controls.
 */
export const tableToolbarToggleGroupClass =
  `inline-flex h-10 shrink-0 items-center rounded-md border ${toolbarControlSurface} p-0.5 gap-0.5 shadow-sm`;

/** Inner segment button inside `tableToolbarToggleGroupClass`. */
export const tableToolbarToggleSegmentClass =
  'flex h-9 w-9 items-center justify-center rounded text-sm font-medium transition-all focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-0 active:opacity-90';

/**
 * `relative` is not decoration. The actions header renders an `sr-only` label,
 * and `sr-only` is `position: absolute`: without a positioned ancestor its
 * containing block is the document, so an ancestor's `overflow` never clips it.
 * On a table wider than its card (Inventory at ~1000px) that 1px span sat past
 * the viewport and gave the whole page a horizontal scrollbar, with the body
 * background showing as a bar beside the app shell (2026-09-26). Making the
 * scroller the containing block keeps every absolute descendant inside it.
 */
export const tableScrollClass = 'relative overflow-x-auto';

export const tableClass = 'w-full text-sm text-left';

/** Solid header (no /opacity) so sticky `<th>` can match and avoid a darker "patch" at the scroll edge. */
/** Column-header strip. Shares the toolbar's tone (`bgLayer2`) on purpose: the
 *  search bar and the `<thead>` are both "table chrome", so they read as one
 *  frame. It ALSO has to differ from the group-header row, which uses
 *  `surface-hover-dark` — when both were the same tone a grouped table looked
 *  like it had no column header at all. */
export const theadClass =
  `border-b border-border-subtle dark:border-border-subtle-dark ${bgLayer2}`;

/** Base cell padding + header typography (merge with alignment / width utilities). */
export const thClass =
  'px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400';

export const tbodyClass =
  'divide-y divide-border-subtle dark:divide-border-subtle-dark';

export const trClass =
  'hover:bg-surface-card-light/50 dark:hover:bg-surface-hover-dark/30 transition-colors group';

/**
 * Default body cell padding + theme-aware text color.
 *
 * Columns frequently pass only `text-xs` / `text-sm` / no color at all
 * in `col.className`; without a default here the text inherits the
 * `<body>` color, which is dark on light mode but stays dark on dark
 * mode too — and disappears. Setting `text-text-main dark:text-white`
 * here gives every cell a sane default that columns can still override
 * (last class wins for utility classes with the same specificity).
 */
export const tdClass = 'px-4 py-3 text-text-main dark:text-white';

/**
 * The one cell of a GROUP row inside a table body («Agente» / «Plugins» in
 * Impresoras → Canales, «Catálogo» / «Ventas» in Estadísticas → Crecimiento).
 * Normal case in the accent colour with a rule above, so it can't be read as
 * a second column-header row (the header is grey uppercase). Give it the full
 * `colSpan`.
 */
export const tableGroupRowCellClass =
  'border-t border-border-subtle bg-primary/5 px-4 py-2 text-sm font-semibold text-primary dark:border-border-subtle-dark dark:bg-primary/10';

/* ── Toolbar clusters ──────────────────────────────────────
 * The two halves of a `tableToolbarClass` row. Both strings were copy-pasted
 * across 14 call sites before they lived here.
 *
 * The wrap and no-wrap right clusters are NOT interchangeable: pages with a
 * view switch or several buttons need their controls to wrap onto a second
 * row, pages with one control must not. Hence two exports rather than a flag.
 */
export const tableToolbarLeftClass =
  'flex flex-col md:flex-row gap-3 items-stretch md:items-center w-full md:w-auto md:flex-1 min-w-0';
export const tableToolbarActionsClass =
  'flex items-center gap-2 w-full md:w-auto md:justify-end';
export const tableToolbarActionsWrapClass =
  'flex flex-wrap items-center gap-2 w-full md:w-auto md:justify-end';

/**
 * Filter chips — the `FilterBar` row (see `components/FilterBar.tsx`).
 *
 * Rounded pills a notch shorter than the h-10 toolbar controls, so a row of
 * them sits centred beside the search input. Neutral = the filter is shown
 * but holds its default (a pinned filter); active = it narrows the list.
 */
export const filterChipBaseClass =
  'h-9 inline-flex items-center gap-1.5 rounded-full border px-3 text-sm font-medium whitespace-nowrap max-w-[18rem] min-w-0 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

export const filterChipNeutralClass =
  'bg-surface-light dark:bg-surface-dark/60 border-border-strong dark:border-border-strong-dark text-slate-600 dark:text-slate-300 hover:border-primary/60';

export const filterChipActiveClass =
  'bg-primary/10 dark:bg-primary/15 border-primary/40 text-primary hover:bg-primary/15 dark:hover:bg-primary/20';

/** The "+ Filtro" / "Filtros" trigger: same pill, dashed while it only adds. */
export const filterAddButtonClass =
  'h-9 inline-flex items-center gap-1.5 rounded-full border border-dashed px-3 text-sm font-medium whitespace-nowrap shrink-0 cursor-pointer transition-colors border-border-strong dark:border-border-strong-dark text-slate-500 dark:text-slate-400 hover:border-primary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
